import { DealActions, DealDetail, FundTradingEntityAssignment, Severity } from 'api'
import { flatten } from 'lodash'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { createTradingEntityAssignmentDetailsSelector } from 'selectors/tradingEntitySelectors'
import { useParametricSelector } from 'utils'
import { canAssignTradingEntities } from 'view/routes/deals/components/utils'
import { createNewAssignments, removeAssignment, updateAssignment, uploadAssignments } from './utils'

const dealIdSelector = (_: {}, dealId: string) => dealId
const tradingEntityAssignmentDetailsSelector = createTradingEntityAssignmentDetailsSelector(dealIdSelector)

export function useFundAssignment(dealDetail: DealDetail | undefined) {
  const assignmentDetails = useParametricSelector(tradingEntityAssignmentDetailsSelector, dealDetail?.deal.dealId ?? '')
  const dispatch = useDispatch()

  return useMemo(() => {
    function saveAssignments(
      assignmentsGetter: (existitingAssignments: FundTradingEntityAssignment[]) => FundTradingEntityAssignment[],
      trimIndex: number | null = null,
      failOnValidation: boolean | null = false
    ) {
      if (!dealDetail?.deal?.dealId || !assignmentDetails || !assignmentDetails.length) return

      dispatch(
        DealActions.saveAssignments({
          dealId: dealDetail.deal.dealId,
          assignments: assignmentsGetter(assignmentDetails.map(d => d.tradingEntityAssignment)),
          failOnValidation: failOnValidation ?? false,
          trimIndex: trimIndex ?? null
        })
      )
    }

    function isAssignEnabled() {
      if (!dealDetail) return false

      const globalValidationIssues = dealDetail.deal.fundAssignment?.validationIssues ?? []
      const allocationValidationIssues = flatten(
        assignmentDetails?.map(assignmentDetail => assignmentDetail.tradingEntityAssignment.validationIssues ?? [])
      )
      return (
        [...globalValidationIssues, ...allocationValidationIssues].filter(validationIssue => validationIssue.severity === Severity.ERROR)
          .length === 0 && canAssignTradingEntities(dealDetail)
      )
    }

    return {
      assignmentDetails,
      isAssignDisabled: !isAssignEnabled(),
      onAddNewAssignment: (assignment: FundTradingEntityAssignment) => saveAssignments(createNewAssignments(assignment)),
      onAssign: (dealId: string) => dispatch(DealActions.assign(dealId)),
      onCancelAssignments: (dealId: string) => dispatch(DealActions.cancelAssignments(dealId)),
      onRemoveAssignment: (assignment: FundTradingEntityAssignment, idx: number) => saveAssignments(removeAssignment(assignment, idx)),
      onSetTrimOrFill: (trimIndex: number) => saveAssignments(assignments => assignments, trimIndex),
      onUpdateAssignment: (update: Partial<FundTradingEntityAssignment>, idx: number) => saveAssignments(updateAssignment(update, idx)),
      onUploadAssignments: (assignments: Partial<FundTradingEntityAssignment>[]) => saveAssignments(uploadAssignments(assignments))
    }
  }, [assignmentDetails, dealDetail, dispatch])
}
